import React, { useState, SyntheticEvent, MouseEvent } from 'react';
import { Card, CardHeader, CardContent, Grid, makeStyles, Slider, Typography, Button, Link } from '@material-ui/core';
import { Alert, Color } from '@material-ui/lab';
import { Link as RouterLink } from 'react-router-dom';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import RestoreIcon from '@material-ui/icons/Restore';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers';
import dayjs from 'dayjs';
import DayjsUtils from '@date-io/dayjs';
import duration, { Duration } from 'dayjs/plugin/duration';
import minMax from 'dayjs/plugin/minMax';
import Clipper, { AdditionalChannel } from './Clipper';
import Periods, { getEditablePeriod } from '../airings/Periods';
import EditorForm from './EditorForm';
import SimpleTextInfo from './SimpleTextInfo';
import { EditorialHighlight as EditorialHighlightType, Airing, Status, Period, Game, Territory } from '../../utils/types';
import { toLocalFormattedString } from '../../utils/dateTransform';
import { isRowValid } from '../../utils/validation';
import useEditorialHighlightSave from '../editorialHighlights/useEditorialHighlightSave';
import { comparePeriodStartTime } from '../editorialHighlights/utils';

dayjs.extend(minMax);
dayjs.extend(duration);

const useStyles = makeStyles((theme) => ({
	spacingRight: {
		marginRight: theme.spacing(1)
	},
	spacingBottom: {
		marginBottom: theme.spacing(2)
	},
	spacingTop: {
		marginTop: theme.spacing(2)
	},
	title: {
		marginBottom: theme.spacing(2)
	},
	airingStatus: {
		marginBottom: theme.spacing(2)
	}
}));

const AIRING_START_BUFFER_MINUTES = 15;
const UX_BUFFER_MINUTES = 30;

export const EditorialHighlights: React.FC<EditorialHighlightsProps> = (props) => {
	const { airings, game, editorialHighlight, highlightType, onAlert } = props;
	const classes = useStyles();
	const [selectedAiring, setSelectedAiring] = useState<Airing>(airings[0]);
	const [duration, setDuration] = useState<number>(3600);
	const [durationStep, setDurationStep] = useState<number>(3600);
	const [saveDisabled, setSaveDisabled] = useState(true);
	const [editorialPeriods, setEditorialPeriods] = useState<Period[] | []>(
		editorialHighlight?.periods.items ? getEditablePeriod(editorialHighlight?.periods.items) : []
	);

	/* Set the start time to the latter of:
        - 15 minutes before the airing start time (any earlier the channel might not be on)
        - 30 minutes ago (post 30 minutes in this should be chosen)
    */
	const getLiveTime = (): string => {
		return dayjs
			.max([dayjs(selectedAiring.startTime).subtract(AIRING_START_BUFFER_MINUTES, 'minute'), dayjs().subtract(UX_BUFFER_MINUTES, 'minute')])
			.toString();
	};
	const [start, setStart] = useState<string>(getLiveTime());

	const saveEditorialHighlight = useEditorialHighlightSave(game.sport ?? null, game.id, selectedAiring);

	if (game.sport?.toLowerCase() === 'golf' && highlightType === 'airing') {
		return (
			<>
				<Typography>
					Sorry, it is not possible to create editorial clips on airing page for golf. Please proceed to create editorial clips for golf on game page
					using the link below:
				</Typography>
				<Link component={RouterLink} to={`/games/${game.id}/editorial-highlight`}>
					<Typography>Game: {game.id}</Typography>
				</Link>
			</>
		);
	}

	let hlsVideoUrl;
	if (selectedAiring.channel?.sourceAssets?.items) {
		const hlsUrl =
			selectedAiring.channel.sourceAssets.items.find((source) => source.id === 'HLS') ||
			selectedAiring.channel.sourceAssets.items.find((source) => source.format === 'HLS');
		if (hlsUrl) hlsVideoUrl = hlsUrl.url;
	}

	const getEndTime = (): string | null => {
		const dt = new Date(start!);
		const day = dayjs(dt);
		if (day.isValid()) {
			return day.add(duration / 60, 'm').toISOString();
		}
		return null;
	};

	const getStartTime = (): string | null => {
		const dt = new Date(start!);
		const day = dayjs(dt);
		return day.isValid() ? day.toISOString() : null;
	};

	const endTime = getEndTime();
	const startTime = getStartTime();

	const url = hlsVideoUrl && endTime && endTime ? `${hlsVideoUrl}?start=${startTime}&end=${endTime}` : '';

	const onWatchClip = (startTime: string, duration: number, channelId: string): void => {
		if (selectedAiring.channelId !== channelId) {
			const wantedAiring = airings.find((airing) => airing.channelId === channelId);
			if (wantedAiring) {
				changeAiring(wantedAiring, startTime, duration);
				return;
			}
		}
		setStart(startTime);
		setDuration(duration);
	};

	const setGameTimePosition = (clip: Period): Period => {
		const gameStartTimestamp: Date = new Date(game.startTime!);
		const start = new Date(clip.source.startTime!);
		const gameTimePosition: Duration = dayjs.duration(Math.floor(dayjs(start).diff(dayjs(gameStartTimestamp), 'm')), 'm');
		const newClip = { ...clip };
		newClip.startGameTime!.position = gameTimePosition.toISOString();
		return newClip;
	};

	const onAddClip = (clip: Period): void => {
		const processedClip = setGameTimePosition(clip);
		let newEditorialPeriods;
		if (editorialPeriods) {
			newEditorialPeriods = [...editorialPeriods];
			const olderClip = newEditorialPeriods.find((currentClip: Period) => currentClip.source.startTime! > processedClip.source.startTime!);
			if (olderClip) {
				const olderClipIndex = newEditorialPeriods.indexOf(olderClip);
				newEditorialPeriods.splice(olderClipIndex, 0, processedClip);
			} else {
				newEditorialPeriods.push(processedClip);
			}
		} else {
			newEditorialPeriods = [processedClip];
		}
		setEditorialPeriods(newEditorialPeriods);
		setSaveEnabled(newEditorialPeriods);
	};

	const transformPeriodData = (period: Period): Period => {
		if (period.startGameTimeEditValue) {
			if (period.startGameTime) {
				period.startGameTime.position = `PT${period.startGameTimeEditValue}M0S`;
			}
		} else {
			period = setGameTimePosition(period);
			period.startGameTimeEditValue = null;
		}
		return period;
	};

	const isEditorialPeriodTimeInvalid = (periodToValudate: Period): { invalid: boolean; message?: string[] } => {
		if (!(typeof periodToValudate.source.startTime == 'string' && typeof periodToValudate.source.endTime == 'string')) {
			return { invalid: false };
		}
		const startTime = dayjs(periodToValudate.source.startTime as string);
		const endTime = dayjs(periodToValudate.source.endTime as string);
		const isPeriodTimeValid = [
			{
				condition: startTime.isAfter(endTime),
				message: 'startTime cannot be after endTime '
			},
			{
				condition: startTime.isSame(endTime),
				message: 'startTime cannot equal to endTime '
			},
			{
				condition: dayjs(Date.now()).isBefore(startTime) || dayjs(Date.now()).isBefore(endTime),
				message: 'startTime and/or endTime cannot be in the future '
			}
		]
			.filter((check) => check.condition)
			.map((checkInvalid) => checkInvalid.message);

		return {
			invalid: isPeriodTimeValid && isPeriodTimeValid.length > 0,
			message: isPeriodTimeValid
		};
	};

	const onRowUpdate = (newData: Period, oldData?: Period): Promise<unknown> => {
		const isPeriodTimeInvalid = isEditorialPeriodTimeInvalid(newData);
		if (isPeriodTimeInvalid.invalid) {
			onAlert(`Error: Invalid Period Value, ${isPeriodTimeInvalid.message}`);
			return Promise.reject();
		}
		if (editorialPeriods && oldData) {
			const newEditorialPeriods = [...editorialPeriods];
			const index = newEditorialPeriods.indexOf(oldData);
			newEditorialPeriods[index] = transformPeriodData(newData);
			const sortedNewEditorialPeriods = newEditorialPeriods.sort(comparePeriodStartTime);
			setEditorialPeriods(sortedNewEditorialPeriods);
			setSaveEnabled(sortedNewEditorialPeriods);
		}
		return Promise.resolve();
	};

	const onRowDelete = (data: Period): Promise<unknown> => {
		if (editorialPeriods) {
			const newEditorialPeriods = [...editorialPeriods];
			const index = newEditorialPeriods.indexOf(data);
			newEditorialPeriods.splice(index, 1);
			setEditorialPeriods(newEditorialPeriods);
			setSaveEnabled(newEditorialPeriods);
		}
		return Promise.resolve();
	};

	const handleEditorialHighlightSave = async (event: SyntheticEvent): Promise<unknown> => {
		event.stopPropagation();
		event.preventDefault();
		if (window.confirm('Are you sure you want to update the editorial highlight?')) {
			setSaveDisabled(true);
			try {
				saveEditorialHighlight(editorialPeriods!);
				onAlert('Editorial Highlight Saved');
			} catch {
				onAlert('Error saving Editorial Highlight');
				setSaveEnabled(editorialPeriods);
			}
		}
		return Promise.resolve();
	};

	const setSaveEnabled = (clipsToSave: Period[]): void => {
		let canSave = true;
		clipsToSave.forEach((clip) => {
			if (!isRowValid(game?.sport ?? '', clip)) {
				canSave = false;
			}
		});
		setSaveDisabled(!canSave);
	};

	const handleAiringChange = (event: MouseEvent, airingId: string): void => {
		const wantedAiring = airings.find((airing) => airing.id === airingId);
		if (wantedAiring) {
			changeAiring(wantedAiring, getLiveTime(), durationStep);
		}
	};

	const changeAiring = (airing: Airing, startTime: string, duration: number): void => {
		setSelectedAiring(airing);
		setStart(startTime);
		setDuration(duration);
	};

	const goToLive = (): void => {
		window.player.pause(); // pauses player to prevent continuation of audio
		setStart(getLiveTime());
		setDuration(3600);
		setDurationStep(3600);
	};

	const goToAiringStart = (): void => {
		window.player.pause(); // pauses player to prevent continuation of audio
		setStart(selectedAiring.startTime);
		setDuration(3600);
		setDurationStep(3600);
	};

	const handleChangeJumpTime = (date: dayjs.Dayjs | null, value?: string | null | undefined): void => {
		if (value && !value.includes('_')) {
			const time = date ? dayjs(date, 'HH:mm') : null;
			if (time && time.isValid()) {
				const newJumpDate = dayjs(start).hour(time.hour()).minute(time.minute()).second(time.second()).valueOf();
				setStart(dayjs(newJumpDate).toString());
				setDuration(3600);
				setDurationStep(3600);
			}
		}
	};

	const getAdditionalChannels = (airingsGame: Game, currentAiring: Airing): AdditionalChannel[] | null => {
		const currentTerritory = currentAiring.channel?.territory;

		if (
			airingsGame.sport?.toLowerCase() !== 'f1' ||
			!airingsGame.airings?.items?.length ||
			airingsGame.airings?.items?.length < 2 ||
			!currentTerritory
		)
			return null;

		const siblingTerritories =
			currentTerritory === ('GB' as unknown as Territory)
				? ['DE' as unknown as Territory, 'IT' as unknown as Territory]
				: ['GB' as unknown as Territory];

		const alternativeAirings = airingsGame.airings.items.filter((airing) => {
			if (airing.id === currentAiring.id) {
				return false;
			} else if (airing.startTime === currentAiring.startTime && siblingTerritories.includes(airing.channel?.territory as Territory)) {
				return true;
			} else {
				return false;
			}
		});

		if (!alternativeAirings || alternativeAirings.length < 1) return null;

		return alternativeAirings.map((additionalAiring) => {
			let hlsVideoUrlAdditional;
			if (additionalAiring.channel?.sourceAssets?.items) {
				const hlsUrlAdditional =
					additionalAiring.channel.sourceAssets.items.find((source) => source.id === 'HLS') ||
					additionalAiring.channel.sourceAssets.items.find((source) => source.format === 'HLS');
				if (hlsUrlAdditional) hlsVideoUrlAdditional = hlsUrlAdditional.url;
			}
			const urlAdditional = hlsVideoUrlAdditional && startTime && endTime ? `${hlsVideoUrlAdditional}?start=${startTime}&end=${endTime}` : null;
			return {
				channel: additionalAiring.channel!.id!,
				url: urlAdditional!
			} as AdditionalChannel;
		});
	};

	const getAiringStatus = (status: Status): JSX.Element => {
		let severity = 'error';
		let text = 'This airing is completed. Are you sure you want to create new clips?';

		switch (status) {
			case Status.SCHEDULED_WITH_MAF:
				severity = 'warning';
				text = 'This airing is scheduled and may have not started yet. Are you sure you want to create new clips?';
				break;
			case Status.LIVE:
				severity = 'success';
				text = 'This airing is live.';
				break;
			case Status.COMPLETED:
				severity = 'error';
				text = 'This airing has completed. Are you sure you want to create new clips?';
				break;
			default:
				break;
		}

		return (
			<Alert className={classes.airingStatus} severity={severity as Color}>
				{text}
			</Alert>
		);
	};

	const isGolf = game.sport?.toLowerCase() === 'golf';

	return (
		<>
			<Card className={classes.spacingBottom}>
				<CardHeader title='Airing Selector' />
				<CardContent>
					<ToggleButtonGroup value={selectedAiring.id} onChange={handleAiringChange} exclusive>
						{airings.map((airing, index) => (
							<ToggleButton key={index} value={airing.id}>
								{airing.channel?.name ?? 'unknown channel'} : {toLocalFormattedString(airing.startTime)}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</CardContent>
			</Card>
			<Card className={classes.spacingBottom}>
				<CardContent>
					{getAiringStatus(selectedAiring.status)}
					{game?.title && (
						<Typography variant='h4' className={classes.title}>
							{game?.title}
						</Typography>
					)}
					<Grid item xs={12} lg={4} className={classes.spacingBottom}>
						{game?.id && (
							<SimpleTextInfo
								label='Game'
								value={game.id}
								to={`/games/${encodeURIComponent(game.id)}`}
								subTo={isGolf ? `/games/${encodeURIComponent(game.id)}/compose-highlight` : undefined}
								subValue={isGolf ? ' [Compose Highlight]' : undefined}
							/>
						)}
						<SimpleTextInfo
							label='Airing'
							value={selectedAiring.id}
							to={`/airings/${encodeURIComponent(selectedAiring.id)}`}
							subTo={!isGolf ? `/airings/${encodeURIComponent(selectedAiring.id)}/compose-highlight` : undefined}
							subValue={!isGolf ? ' [Compose Highlight]' : undefined}
						/>
					</Grid>
					<Button
						onClick={(): void => goToAiringStart()}
						variant='contained'
						className={`${classes.spacingRight} ${classes.spacingBottom}`}
						startIcon={<RestoreIcon />}>
						Airing Start
					</Button>
					<Button
						onClick={(): void => goToLive()}
						variant='contained'
						className={`${classes.spacingRight} ${classes.spacingBottom}`}
						startIcon={<LiveTvIcon />}>
						Live
					</Button>
					<MuiPickersUtilsProvider utils={DayjsUtils}>
						<Grid container item xs={12} lg={6} className={classes.spacingBottom}>
							<Grid container item xs={12} lg={6}>
								<Grid item xs={6}>
									<KeyboardDatePicker
										disableToolbar
										variant='inline'
										margin='normal'
										id='startTime'
										name='startTime'
										label='Start Date'
										value={start}
										onChange={(date: dayjs.Dayjs | null): void => setStart(date!.toISOString())}
										KeyboardButtonProps={{
											'aria-label': 'change date'
										}}
									/>
								</Grid>
								<Grid item xs={6}>
									<KeyboardTimePicker
										margin='normal'
										id='jumpTime'
										label='Time'
										ampm={false}
										format='HH:mm'
										value={start}
										keyboardIcon={<QueryBuilderIcon />}
										onChange={handleChangeJumpTime}
										KeyboardButtonProps={{
											'aria-label': 'change time'
										}}
									/>
								</Grid>
							</Grid>
							<Grid item xs={12} lg={6}>
								<Typography variant='caption' id='continuous-slider'>
									Duration
								</Typography>
								<Slider
									id='durationTime'
									name='durationTime'
									value={Number(durationStep)}
									step={100}
									min={100}
									max={10800}
									onChange={(event, value): void => setDurationStep(value as number)}
									onChangeCommitted={(event, value): void => setDuration(value as number)}
									valueLabelDisplay='auto'
									marks={[
										{
											value: 60,
											label: '1m'
										},
										{
											value: 1800,
											label: '30m'
										},
										{
											value: 3600,
											label: '1h'
										},
										{
											value: 7200,
											label: '2h'
										},
										{
											value: 10800,
											label: '3h'
										}
									]}
									aria-labelledby='non-linear-slider'
								/>
							</Grid>
						</Grid>
					</MuiPickersUtilsProvider>
					{url ? (
						<Clipper
							channelId={selectedAiring.channelId}
							url={url}
							additionalChannels={getAdditionalChannels(game, selectedAiring)}
							start={start}
							duration={duration}
							onWatchClip={onWatchClip}
							onAddClip={onAddClip}
						/>
					) : (
						<Typography>Error with channel URL</Typography>
					)}
				</CardContent>
			</Card>
			<EditorForm onSubmit={handleEditorialHighlightSave} submitText='Save Editorial Highlight' saveDisabled={saveDisabled}>
				<Periods
					title='Editorial Highlight Clips'
					sport={game.sport}
					periods={editorialPeriods || []}
					onWatchClip={onWatchClip}
					onRowUpdate={onRowUpdate}
					onRowDelete={onRowDelete}
					teams={game.teams?.items ?? null}
					leagueId={game?.league?.id}
				/>
			</EditorForm>
		</>
	);
};

type EditorialHighlightsProps = {
	airings: Airing[];
	game: Game;
	highlightType: 'game' | 'airing';
	editorialHighlight: EditorialHighlightType | null;
	onAlert: (message: string) => void;
};

export default EditorialHighlights;
